import { ApiError } from '@gorila-shared-ui/components';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ZONE_FENCE_IDS } from '../../constants/zoneFences';
import useEffectOnce from '../../hooks/useEffectOnce';
import { getPublicZoneFencesFileKey, getZoneFencesFile, getZoneFencesFileKey } from '../../services/zoneFencesService';
import { isPublicPageState } from '../../storage/app';
import { zoneFencesGeojsonState } from '../../storage/map';

export function ZoneFencesProvider() {
  const setZoneFencesGeojson = useSetRecoilState(zoneFencesGeojsonState);
  const isPublicPage = useRecoilValue(isPublicPageState);
  const { shareLinkId } = useParams();

  useEffectOnce(() => {
    const handleFileRead = (event: any, zoneType: string) => {
      const content = JSON.parse(event.target.result);
      setZoneFencesGeojson((prev) => (prev ? { ...prev, [zoneType]: content } : { [zoneType]: content }));
    };

    const getPublicZoneFencesFile = async (url: string) => {
      const response = await fetch(url);
      if (response.status === 200 && response.body) {
        return {
          file: await new Response(response.body).blob(),
          error: undefined,
        };
      }
      return {
        file: undefined,
        error: '',
      };
    };

    const handleResponse = async ({
      url,
      key,
      zoneType,
      error,
    }: {
      key?: string;
      url?: string;
      zoneType: string;
      error?: ApiError;
    }) => {
      if (!error && (key || url)) {
        const { file, error: fileError } = await (url ? getPublicZoneFencesFile(url) : getZoneFencesFile(key!));
        if (!fileError && file) {
          const reader = new FileReader();
          reader.onload = (event: any) => {
            handleFileRead(event, zoneType);
          };
          reader.readAsText(file);
        } else {
          setZoneFencesGeojson(undefined);
        }
      } else {
        setZoneFencesGeojson(undefined);
      }
    };

    const loadZoneFenceFile = async (zoneType: string) => {
      if (isPublicPage && shareLinkId) {
        const { url, error } = await getPublicZoneFencesFileKey(shareLinkId, zoneType);
        await handleResponse({ url, zoneType, error });
      } else {
        const { key, error } = await getZoneFencesFileKey(zoneType);
        await handleResponse({ key, zoneType, error });
      }
    };

    ZONE_FENCE_IDS.forEach((zoneFenceId) => {
      loadZoneFenceFile(zoneFenceId);
    });
  });

  return null;
}
